import {
  LogLevel,
  type Configuration,
  type PopupRequest,
  type RedirectRequest,
} from '@azure/msal-browser';

/**
 * Entra configuration for the **workforce** tenant — see docs/identity.md.
 *
 * ⚠️ This is the file OD-18 is about. The customer app has its own copy
 * pointing at the CIAM tenant, and the two are deliberately not shared:
 *
 * - Different **variables** (`VITE_STAFF_*`), so a misconfigured environment
 *   fails loudly rather than silently authenticating staff against the
 *   customer directory, or vice versa.
 * - Different **origins** (`staff.mainlinepro.net` vs `app.mainlinepro.net`,
 *   and :5174 vs :5173 locally), so `sessionStorage` is isolated by the
 *   browser itself. There is no shared `PublicClientApplication`, no shared
 *   cache, and no run-time branch deciding which authority to use — the
 *   separation is structural, which is the whole point of the decision.
 *
 * Every value is supplied at build time, exactly like the customer app (M0-16).
 */
const clientId = import.meta.env.VITE_STAFF_ENTRA_CLIENT_ID ?? '';
const authority = import.meta.env.VITE_STAFF_ENTRA_AUTHORITY ?? '';
const apiScope = import.meta.env.VITE_STAFF_API_SCOPE ?? '';

/** True when the console has enough configuration to attempt sign-in. */
export const isAuthConfigured = Boolean(clientId && authority && apiScope);

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority,
    // NOTE: no `knownAuthorities`, and that is not an oversight. The customer
    // app needs it because CIAM authorities live on `*.ciamlogin.com`, which
    // MSAL does not trust by default. The workforce tenant is on
    // `login.microsoftonline.com`, which MSAL already knows — listing it here
    // would be noise implying a trust decision that is not being made.
    redirectUri: window.location.origin + '/',
    postLogoutRedirectUri: window.location.origin + '/',
  },
  cache: {
    // sessionStorage, never localStorage — the same reasoning as the customer
    // app, and stronger here: a staff token reads across every tenant, so it
    // has no business outliving the tab on a shared support machine.
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Error,
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (!containsPii && level === LogLevel.Error) {
          console.error('[msal:staff]', message);
        }
      },
    },
  },
};

/** Scopes requested at sign-in. */
export const loginRequest: RedirectRequest & PopupRequest = {
  scopes: [apiScope],
};

/** Scopes for calling the API; acquired silently once signed in. */
export const apiTokenRequest = {
  scopes: [apiScope],
};
